import { useQuery } from "@apollo/client"
import clsx from "clsx"
import { Form, Formik } from "formik"
import pluralize from "pluralize"
import { useSearchParams } from "react-router-dom"
import { getFragmentData, gql } from "~/__generated__"
import useAdminUsersParamValues from "~/hooks/useAdminUsersParamValues"
import AdminSearchFields from "~/screens/AdminScreen/components/AdminSearchFields"
import Error from "~/ui/Error"
import { H1 } from "~/ui/Headings"
import Loading from "~/ui/Loading"
import AdminUsersTable, {
  AdminUsers_QueryFragment,
} from "./components/AdminUsersTable"

export const AdminUsersScreen_Query = gql(`
  query AdminUsersScreen_Query($admin: Boolean, $page: Int, $search: String, $limit: Int) {
    ...AdminUsers_QueryFragment
  }
`)

export const USERS_LIMIT = 12

export const UserSearch = () => {
  const [searchParams, setSearchParams] = useAdminUsersParamValues()

  return (
    <Formik
      initialValues={{ search: searchParams.search || "" }}
      onSubmit={(values) => {
        setSearchParams({
          search: values.search || undefined,
          page: undefined,
        })
      }}
    >
      <Form>
        <AdminSearchFields placeholder="Search by name, email, company" />
      </Form>
    </Formik>
  )
}

const AdminUsersIndexScreen = () => {
  const [searchParams] = useSearchParams()
  const page = searchParams.get("page")
  const result = useQuery(AdminUsersScreen_Query, {
    variables: {
      admin: false,
      search: searchParams.get("search") || undefined,
      page: page ? Number(page) : 1,
      limit: USERS_LIMIT,
    },
  })

  const { loading, error } = result
  const data = loading ? result.previousData : result.data

  if (loading && !data) return <Loading />
  if (error || !data) return <Error error={error} />

  const { users } = getFragmentData(AdminUsers_QueryFragment, data)
  const totalCount = users.users.totalCount

  return (
    <div>
      <div
        className={clsx(
          "mb-6 flex flex-col gap-6",
          "lg:mb-0 lg:flex-row lg:justify-between"
        )}
      >
        <div>
          <H1>Users</H1>
        </div>
        <div>
          <UserSearch />
        </div>
      </div>
      <div className="mb-6 flex flex-col gap-6 text-base">
        <div className="my-1">
          <b>
            {totalCount.toLocaleString("en-US", {
              maximumFractionDigits: 0,
            })}
          </b>{" "}
          {pluralize("user", totalCount)}
        </div>
      </div>
      <div
        className={clsx({
          "animate-pulse opacity-90": loading,
        })}
      >
        {users.users.edges.length > 0 && (
          <AdminUsersTable
            users={users.users.edges.map((edge) => edge.node)}
            totalCount={totalCount}
            page={users.page}
          />
        )}
      </div>
    </div>
  )
}

export default AdminUsersIndexScreen
